import { PrismicRichText } from '@prismicio/react';
import Image from 'next/image';
import { fieldHasContent, transformProperties } from 'remote-com-shared/domains/prismic/helpers';
import styled, { css } from 'styled-components';

import 'plyr/dist/plyr.css';
import LeftCircle from '../assets/circle-left.png';
import RightCircle from '../assets/circle-right.png';
import VideoThumbnail from '../assets/video-thumbnail.png';

import {
  SectionLabel,
  SectionTitleComponent,
  OverviewDescriptionComponent,
} from '@/src/components/OverviewSection/OverviewSectionRow';
import { tabletBreakpoint, fromLargeDesktopBreakpoint } from '@/src/domains/shared/breakpoints';
import { components } from '@/src/views/InternationalGlobalPayroll/slices//GlobalPayrollSlice';
import {
  PlayVideoArea,
  BestPracticesContainer,
  Description as DescriptionComponent,
  Title as TitleComponent,
} from '@/src/views/InternationalGlobalPayroll/slices/BestPracticesSlice';

export const Section = styled(BestPracticesContainer)`
  margin-bottom: -350px;
`;

export const Description = styled(DescriptionComponent)`
  color: var(--colors-bayoux);
`;

export const Title = styled(TitleComponent)`
  max-width: 817px;
  margin: auto;
  text-align: center;
  padding: 100px 0 20px;
`;

export const BlockImage = styled.div`
  min-width: 80px;
  margin-right: 23px;

  ${tabletBreakpoint(css`
    margin-bottom: 20px;
  `)}
`;

export const BlockContent = styled.div`
  display: flex;
  margin-top: -20px;

  ${tabletBreakpoint(css`
    flex-wrap: wrap;
    justify-content: center;
  `)}

  &:last-child {
    margin-top: -10px;
  }
`;

export const BestPracticeBlock = styled.div`
  padding: 20px 0 55px;
  display: flex;
  border-left: 2px solid #d0caf7;

  &:first-child {
    padding-top: 0;
  }

  &:last-child {
    border: none;
    padding-top: 0;
  }

  ${tabletBreakpoint(css`
    width: 90%;
    margin-left: 20px;
  `)}
`;

export const StepTitle = styled.div`
  ${({ theme }) => theme.typography.titleSM};
  color: #000000;

  & > * {
    font-size: inherit;
    font-weight: inherit;
    line-height: inherit;
    margin: inherit;
  }
`;

export const SectionHeader = styled.div`
  text-align: center;
  padding-bottom: 70px;
  margin: auto;

  ${SectionTitleComponent} {
    ${({ theme }) => theme.typography.scale3}
  }

  ${OverviewDescriptionComponent} {
    ${({ theme }) => theme.typography.body}
    color: var(--colors-bayoux);
    margin-top: 32px;
  }
`;

export const BlockNumber = styled.div`
  min-width: 45px;
  height: 45px;
  border: 2px solid var(--colors-irisBlue);
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 36px;
  color: var(--colors-irisBlue);
  ${({ theme }) => theme.typography.titleSM}
  margin-right: 33px;
  margin-left: -25px;
  background-color: var(--colors-blank);
`;

export const PlayVideoAreaWrapper = styled.div`
  position: relative;
`;

export const LeftImageWrapper = styled.div`
  position: absolute;
  left: -14%;

  ${tabletBreakpoint(css`
    display: none;
  `)}
`;

const RightImageWrapper = styled.div`
  display: none;

  ${fromLargeDesktopBreakpoint(css`
    display: block;
    position: absolute;
    right: -14%;
    bottom: -10%;
  `)};
`;

export function VideoPlayer() {
  return (
    <PlayVideoAreaWrapper>
      <LeftImageWrapper>
        <Image
          src={LeftCircle.src}
          alt={LeftCircle.alt || ''}
          width={LeftCircle.width}
          height={LeftCircle.height}
          quality="100"
        />
      </LeftImageWrapper>
      <RightImageWrapper>
        <Image
          src={RightCircle.src}
          alt={RightCircle.alt || ''}
          width={RightCircle.width}
          height={RightCircle.height}
          quality="100"
        />
      </RightImageWrapper>
      <PlayVideoArea aria-label="Global Payroll" illustration={VideoThumbnail} />
    </PlayVideoAreaWrapper>
  );
}

export function BestPracticesSlice({ slice }) {
  const { primary, fields, context } = transformProperties(slice);
  const { videoEmbedTitle } = primary;
  const { servicesGlobalPayrollView } = context;

  const sectionHeader = servicesGlobalPayrollView.body.find(
    ({ sliceType }) => sliceType === 'partners_section'
  );

  const { partnersDescription, partnersTitle, partnersLabel } = sectionHeader.primary;

  const hasHeading =
    fieldHasContent(partnersLabel) ||
    fieldHasContent(partnersTitle) ||
    fieldHasContent(partnersDescription);

  return (
    <Section id="best-practices">
      {hasHeading && (
        <SectionHeader>
          {fieldHasContent(partnersLabel) && <SectionLabel>{partnersLabel}</SectionLabel>}
          {fieldHasContent(partnersTitle) && (
            <SectionTitleComponent>
              <PrismicRichText field={partnersTitle} />
            </SectionTitleComponent>
          )}
          {fieldHasContent(partnersDescription) && (
            <OverviewDescriptionComponent>
              <PrismicRichText field={partnersDescription} />
            </OverviewDescriptionComponent>
          )}
        </SectionHeader>
      )}
      <div>
        {fields?.map(({ stepTitle, stepDescription, illustration }, index) => (
          <BestPracticeBlock key={index}>
            <BlockNumber>{index + 1}</BlockNumber>
            <BlockContent>
              <BlockImage>
                {illustration && (
                  <Image
                    src={illustration.url}
                    alt={illustration.alt || ''}
                    width={illustration.dimensions.width}
                    height={illustration.dimensions.height}
                    quality="100"
                  />
                )}
              </BlockImage>
              <div>
                {fieldHasContent(stepTitle) && (
                  <StepTitle>
                    <PrismicRichText field={stepTitle} />
                  </StepTitle>
                )}
                {fieldHasContent(stepDescription) && (
                  <Description>
                    <PrismicRichText field={stepDescription} components={components} />
                  </Description>
                )}
              </div>
            </BlockContent>
          </BestPracticeBlock>
        ))}
      </div>
      {fieldHasContent(videoEmbedTitle) && (
        <Title>
          <PrismicRichText field={videoEmbedTitle} />
        </Title>
      )}
      <VideoPlayer />
    </Section>
  );
}
